/**
 * ProvidersSection - Provider readiness and sign-in, independent of any thread.
 *
 * Provider credentials live on the server and are shared by everyone using it,
 * so this is gated on `p.workspace_terminal`: the same permission the server
 * requires to open the workspace terminal the login actually runs in. Anyone
 * holding it could run the login by hand anyway; anyone without it must not be
 * able to swap the credentials the whole team depends on.
 */
import {
  PROVIDER_DISPLAY_NAMES,
  type ServerConfig,
  type ServerProviderStatus,
} from "@taskin-code/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { useAuthStore } from "../../auth/authStore";
import { readNativeApi } from "../../nativeApi";
import { serverConfigQueryOptions, serverQueryKeys } from "../../lib/serverReactQuery";
import { workspaceThreadId } from "../../workspaceStore";
import { Button } from "../ui/button";
import { cn } from "~/lib/utils";

/**
 * This viewport only appears while signing in to a provider, but importing it
 * eagerly pulled all of xterm into the entry chunk — the settings route is not
 * code-split, so every first paint paid for a terminal most visits never open.
 */
const TerminalViewport = lazy(() =>
  import("../terminal/TerminalViewport").then((module) => ({
    default: module.TerminalViewport,
  })),
);

/**
 * Stable workspace thread that hosts provider logins. Workspace terminals are
 * not tied to a project or a chat thread, which is what lets this screen work
 * with no thread open at all.
 */
const PROVIDER_AUTH_WORKSPACE_ID = "provider-auth";

/** How long to keep re-checking after a login starts, and how often. */
const POLL_WINDOW_MS = 180_000;
const POLL_INTERVAL_MS = 5_000;

type StatusTone = "ready" | "attention" | "unknown";

function statusTone(status: ServerProviderStatus): StatusTone {
  if (status.status === "ready") return "ready";
  return status.authStatus === "unauthenticated" ? "attention" : "unknown";
}

function statusLabel(status: ServerProviderStatus): string {
  if (status.status === "ready") return "Ready";
  if (status.authStatus === "unauthenticated") return "Sign in required";
  return status.available ? "Limited" : "Unavailable";
}

function formatCheckedAt(checkedAt: string): string {
  const checkedMs = Date.parse(checkedAt);
  if (Number.isNaN(checkedMs)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - checkedMs) / 1000));
  if (seconds < 60) return `checked ${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `checked ${minutes}m ago`;
  return `checked ${Math.round(minutes / 60)}h ago`;
}

export function ProvidersSection() {
  const canManage = useAuthStore((state) => state.globalPermissions.has("p.workspace_terminal"));
  const queryClient = useQueryClient();
  const serverConfigQuery = useQuery(serverConfigQueryOptions());
  const providers = useMemo(
    () => serverConfigQuery.data?.providers ?? [],
    [serverConfigQuery.data?.providers],
  );

  const [signingInProvider, setSigningInProvider] = useState<string | null>(null);
  const [pollDeadline, setPollDeadline] = useState<number | null>(null);
  const [pollTick, setPollTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const threadId = useMemo(() => workspaceThreadId(PROVIDER_AUTH_WORKSPACE_ID), []);
  const homeDir = serverConfigQuery.data?.homeDir ?? null;

  const refreshProviders = useCallback(async () => {
    const api = readNativeApi();
    if (!api) return;
    setIsRefreshing(true);
    try {
      const result = await api.server.refreshProviders();
      queryClient.setQueryData<ServerConfig>(serverQueryKeys.config(), (current) =>
        current ? { ...current, providers: result.providers } : current,
      );
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const signInProvider = useCallback(
    async (provider: ServerProviderStatus) => {
      const api = readNativeApi();
      const command = provider.reauthCommand;
      if (!api || !command || !homeDir) return;

      setSigningInProvider(provider.provider);
      setPollDeadline(Date.now() + POLL_WINDOW_MS);

      const terminalId = `provider-auth-${provider.provider}`;
      await api.terminal.open({ threadId, terminalId, cwd: homeDir });
      await api.terminal.write({ threadId, terminalId, data: `${command}\r` });
    },
    [homeDir, threadId],
  );

  // Collapse the sign-in card as soon as the provider reports healthy.
  //
  // Kept separate from the polling effect below on purpose: the status can also
  // arrive over the `server.providerStatusesUpdated` push, and tying this to the
  // poll deadline left the card stuck on "signing in" whenever the deadline was
  // cleared before the status landed.
  useEffect(() => {
    if (signingInProvider === null) return;
    const signedIn = providers.some(
      (entry) => entry.provider === signingInProvider && entry.status === "ready",
    );
    if (signedIn) {
      setSigningInProvider(null);
      setPollDeadline(null);
    }
  }, [providers, signingInProvider]);

  // The login finishes in the terminal with no completion signal, so poll until
  // the provider reports healthy or the window elapses.
  useEffect(() => {
    if (pollDeadline === null) return;
    if (Date.now() >= pollDeadline) {
      setPollDeadline(null);
      return;
    }
    const timer = setTimeout(() => {
      void refreshProviders();
      setPollTick((value) => value + 1);
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pollDeadline, pollTick, refreshProviders]);

  if (providers.length === 0) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        Aún no hay estado de providers. Vuelve en unos segundos.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Providers</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Estado de los CLI de agentes en este servidor. Las credenciales son del servidor y las
          comparte todo el equipo: al iniciar sesión cambias la cuenta que usan todos.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {providers.map((provider) => {
          const tone = statusTone(provider);
          const label = PROVIDER_DISPLAY_NAMES[provider.provider] ?? provider.provider;
          const isSigningIn = signingInProvider === provider.provider;
          const canSignIn = canManage && provider.reauthCommand !== undefined;

          return (
            <div
              key={provider.provider}
              className="rounded-md border border-border/60 bg-card/40 p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{label}</span>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.08em]",
                        tone === "ready" && "bg-success/12 text-success",
                        tone === "attention" && "bg-destructive/12 text-destructive",
                        tone === "unknown" && "bg-warning/12 text-warning",
                      )}
                    >
                      {statusLabel(provider)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {provider.message ?? "Listo para usarse."}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                    {formatCheckedAt(provider.checkedAt)}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isRefreshing}
                    onClick={() => void refreshProviders()}
                  >
                    {isRefreshing ? "Comprobando…" : "Refresh"}
                  </Button>
                  {canSignIn ? (
                    <Button
                      size="sm"
                      variant={tone === "attention" ? "default" : "outline"}
                      disabled={isSigningIn}
                      onClick={() => void signInProvider(provider)}
                    >
                      {isSigningIn
                        ? "Iniciando sesión…"
                        : provider.authStatus === "unauthenticated"
                          ? "Sign in"
                          : "Re-authenticate"}
                    </Button>
                  ) : null}
                </div>
              </div>

              {isSigningIn && homeDir ? (
                <div className="mt-3 h-64 overflow-hidden rounded-md border border-border/60">
                  <Suspense fallback={null}>
                    <TerminalViewport
                      threadId={threadId}
                      terminalId={`provider-auth-${provider.provider}`}
                      terminalLabel={`${label} sign-in`}
                      cwd={homeDir}
                      onSessionExited={() => setSigningInProvider(null)}
                      onTerminalMetadataChange={() => {}}
                      onTerminalActivityChange={() => {}}
                      onAddTerminalContext={() => {}}
                      focusRequestId={0}
                      autoFocus
                      isVisible
                    />
                  </Suspense>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {!canManage ? (
        <p className="text-xs text-muted-foreground">
          Necesitas el permiso <code>p.workspace_terminal</code> para iniciar sesión en un provider.
          Pide a un administrador que lo haga.
        </p>
      ) : null}
    </div>
  );
}
